import { Trash2, ThumbsUp, ThumbsDown, Heart, Eye } from "lucide-react";
import { useState } from "react";
import CustomAvatar from "../community/CustomAvatar";
import { AdminPanelComment } from "../../lib/community";
import { Link } from "react-router";
import ProfilePopup from "../ui/ProfilePopup";
import MediaSlider from "../community/MediaSlider";

type ParentCommentProps = {
  parent: AdminPanelComment;
  onDelete: (commentId: string) => void;
};

export const ParentComment = ({ parent, onDelete }: ParentCommentProps) => {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const toggle = () => {
    setExpanded(!expanded);
  };

  const commentMedia = parent.media
    ? parent.media.map((e) => JSON.parse(e))
    : [];

  return (
    <div className="mt-2 mb-4">
      <button
        onClick={toggle}
        className="text-sm text-gray-700 bg-gray-100 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 mb-4"
      >
        <span>
          Replying to <strong>{parent.authorData?.username ?? "user"}</strong>'s
          comment
        </span>
      </button>

      {expanded && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="">
            <div className="flex gap-4 w-full">
              <div
                className="relative"
                onMouseEnter={() => {
                  setHovered(true);
                }}
                onMouseLeave={() => {
                  setHovered(false);
                }}
              >
                <Link to={`/user/${parent.authorData.user_id}`}>
                  <CustomAvatar userData={parent.authorData} />
                  {hovered && (
                    <ProfilePopup
                      authorData={parent.authorData}
                      authorId={parent.authorData.user_id}
                    />
                  )}
                </Link>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900">
                    {parent.authorData?.username ?? "Unknown"}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mb-2">
                  {new Date(parent.created_at).toLocaleString()}
                </div>
                <div className="mb-2">
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      parent.is_deleted
                        ? "bg-red-100 text-red-700"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {parent.is_deleted ? "Deleted" : "Published"}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-800 leading-relaxed">
              {parent.content}
            </p>
            {commentMedia?.length > 0 && (
              <div className="mt-1 sm:mt-2">
                <MediaSlider media={commentMedia} size={5} blocks={6} />
              </div>
            )}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {!parent.is_deleted && (
                  <button
                    className="text-red-500 text-sm hover:text-red-700 flex items-center gap-1"
                    onClick={() => onDelete(parent.id)}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

type CommentProps = {
  comment: AdminPanelComment;
  onDelete: (commentId: string) => void;
};

export const Comment = ({ comment, onDelete }: CommentProps) => {
  const { authorData } = comment;
  const [hovered, setHovered] = useState(false);
  const username = authorData?.username ?? "Unknown";
  const rating = authorData?.user_rating?.score ?? null;
  const commentMedia = comment.media
    ? comment.media.map((e) => JSON.parse(e))
    : [];
  return (
    <div>
      <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
        {/* Parent (reply) indicator */}
        {comment.parent && (
          <ParentComment parent={comment.parent} onDelete={onDelete} />
        )}

        <div className="flex items-start gap-3">
          <div
            className="relative"
            onMouseEnter={() => {
              setHovered(true);
            }}
            onMouseLeave={() => {
              setHovered(false);
            }}
          >
            <Link to={`/user/${comment.authorData.user_id}`}>
              <CustomAvatar userData={comment.authorData} />
              {hovered && (
                <ProfilePopup
                  authorData={comment.authorData}
                  authorId={comment.authorData.user_id}
                />
              )}
            </Link>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-gray-900">{username}</span>
              {rating !== null && (
                <span className="text-xs text-gray-500">Rating: {rating}</span>
              )}
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
              <span>{new Date(comment.created_at).toLocaleString()}</span>
            </div>

            {!comment.is_deleted ? (
              <div className="mb-3">
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                  Published
                </span>
              </div>
            ) : (
              <div className="mb-3">
                <span className="bg-red-100 text-green-800 text-xs px-2 py-1 rounded">
                  Deleted
                </span>
              </div>
            )}
          </div>
        </div>
        <p className="text-gray-900 mb-4 leading-relaxed">{comment.content}</p>

        {commentMedia?.length > 0 && (
          <div className="mt-1 sm:mt-2">
            <MediaSlider media={commentMedia} size={5} blocks={6} />
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!comment.is_deleted && (
              <>
                <button
                  className="text-red-500 text-sm hover:text-red-700 flex items-center gap-1"
                  onClick={() => onDelete(comment.id)}
                >
                  <Trash2 size={14} />
                  Delete
                </button>
                <Link to={`/post/${comment.post_id}?comment=${comment.id}`}>
                  <button className="text-blue-500 text-sm hover:text-blue-700 flex items-center gap-1">
                    <Eye size={14} />
                    See
                  </button>
                </Link>
              </>
            )}
          </div>

          {/* TODO: likes count add there link */}
          {/* <div className="flex items-center gap-4">
								<div className="flex items-center gap-2">
									<ThumbsUp size={16} className="text-gray-400" />
									<span className="text-sm text-gray-500">0</span>
								</div>
								<div className="flex items-center gap-2">
									<ThumbsDown size={16} className="text-gray-400" />
									<span className="text-sm text-gray-500">0</span>
								</div>
								<Heart size={16} className="text-gray-400 hover:text-red-500 cursor-pointer" />
							</div> */}
        </div>
      </div>
    </div>
  );
};
